import React, { useCallback, useState } from "react";
import {
  Datagrid,
  TextField,
  Filter,
  TextInput,
  SelectInput,
  CreateButton,
  FunctionField,
  Record as RaRecord,
  useTranslate,
  Responsive,
  SimpleList,
  Identifier,
} from "react-admin";
import "../../common/styles/list-style.css";
import "./DriversList.css";
import IndeterminateCheckBoxIcon from "@material-ui/icons/IndeterminateCheckBox";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import AndroidOutlinedIcon from "@material-ui/icons/AndroidOutlined";
import HelpOutlineOutlinedIcon from "@material-ui/icons/HelpOutlineOutlined";
import AppleIcon from "@material-ui/icons/Apple";
import {
  Box,
  useTheme,
  Theme,
  Typography,
  Tooltip,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@material-ui/core";
import { useSelector } from "react-redux";
import { AppState } from "../../../common/types";
import { useListContext } from "react-admin";
import { ActionsField } from "../../common/data-grid-fields/ActionsField";
import { ArrayField } from "../../common/data-grid-fields/ArrayField";
import { IResourceListProps } from "../../common/ra-types";
import { useSearchFields } from "../../../common/hooks/useSearchFields";
import { ProLogsEmpty } from "../../common/list/ProLogsEmpty";
import AssignmentIndOutlinedIcon from "@material-ui/icons/AssignmentIndOutlined";
import { DriverListDto } from "../../../common/data/service-proxies";
import permissionNames from "../../../common/auth/access-control/permissionNames";
import { ChangeDriverPasswordAction } from "./ChangeDriverPasswordAction";
import { ViewDriverPasswordAction } from "./ViewDriverPasswordAction";
import { useActionPermissions } from "../../../common/auth/useActionPermissions";
import { useFieldPermissions } from "../../../common/hooks/useFieldPermissions";
import { protectedEntities } from "../../../common/auth/permissions";
import { FieldPermissionsContext } from "../../common/field-permissions/FieldPermissionsContext";
import { ProtectedElement } from "../../common/field-permissions/ProtectedElement";
import { DefaultProLogsList } from "../../common/list/DefaultProLogsList";
import { useResetListSearchField } from "../../../common/hooks/useResetListSearchField";
import { useAppData } from "../../../common/hooks/useAppData";
import VisibilityOutlinedIcon from "@material-ui/icons/VisibilityOutlined";
import EmailIcon from "@material-ui/icons/Email";
import GroupIcon from "@material-ui/icons/Group";
import PhoneIcon from "@material-ui/icons/Phone";
import { AppbarGroupsMultipleFilter } from "../../common/data-grid-inputs/AppBarGroupsMultipleFilter";

const ListFilter = (props: any) => {
  useSearchFields(props, ["driverName", "email"]);
  useResetListSearchField(props);
  const { loading } = useListContext();
  const companies = useAppData((x) => x.companies);
  return (
    <Filter {...props}>
      {loading && (
        <TextInput
          className="drivers-list-filter search-box"
          label="resources.common.labels.search"
          source="q"
          alwaysOn
          autoComplete="off"
        />
      )}
      {!loading && (
        <TextInput
          className="drivers-list-filter search-box"
          label="resources.common.labels.search"
          source="q"
          alwaysOn
          autoFocus
          autoComplete="off"
        />
      )}
      <SelectInput
        className="filter-box"
        label="resources.admin-drivers.labels.filterByDriverStatus"
        allowEmpty
        source="status"
        alwaysOn
        choices={[
          { id: "Active", name: "Active Drivers" },
          { id: "Inactive", name: "Inactive Drivers" },
        ]}
      />
      <SelectInput
        className="filter-box"
        label="Filter by company"
        allowEmpty
        source="companyId"
        alwaysOn
        choices={companies}
      />
      <SelectInput
        className="filter-box"
        label="Filter by app type"
        allowEmpty
        source="mobileAppType"
        alwaysOn
        choices={[
          { id: 1, name: "Android" },
          { id: 2, name: "Apple" },
        ]}
      />
    </Filter>
  );
};

const ListActions = () => {
  const { basePath } = useListContext();
  const permissions = useActionPermissions();
  return (
    <>
      {permissions &&
        permissions[permissionNames.admin.drivers.main] &&
        permissions[permissionNames.admin.drivers.create] && (
          <CreateButton basePath={basePath} />
        )}
    </>
  );
};

export const DriversList = (props: IResourceListProps) => {
  const translate = useTranslate();
  const themeName = useSelector((state: AppState) => state.theme);
  const theme: Theme = useTheme();
  const permissions = useActionPermissions();
  const fieldPermissions = useFieldPermissions(
    protectedEntities.Driver,
    (x) => x.list
  );

  const [selectedGroups, setSelectedGroups] = useState<Array<Identifier>>([]);
  const handleSelectedGroupsChange = useCallback((e: Identifier[]) => {
    setSelectedGroups(e);
  }, []);

  interface SimpleListProps {
    record?: RaRecord;
  }

  const RenderPrimaryTextForSimpleList = ({ record }: SimpleListProps) => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          gap: "10px",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        {record ? record.driverId + " | " + record.driverName : null}
        <FunctionField<DriverListDto & RaRecord>
          source="status"
          headerClassName="centered-content"
          label="resources.common.labels.active"
          render={(record) => (
            <Box className="centered-content">
              {record?.status === "Active" ? (
                <CheckBoxIcon
                  className={record.status}
                  style={{
                    color:
                      themeName === "dark"
                        ? theme.palette.success.dark
                        : theme.palette.success.light,
                  }}
                />
              ) : (
                <IndeterminateCheckBoxIcon
                  className={record?.status}
                  style={{
                    color:
                      themeName === "dark"
                        ? theme.palette.error.dark
                        : theme.palette.error.light,
                  }}
                />
              )}
            </Box>
          )}
        />
      </div>
    );
  };

  const RenderSecondaryTextForSimpleList = ({ record }: SimpleListProps) => {
    return (
      <div
        style={{
          marginTop: "14px",
          display: "flex",
          flexDirection: "column",
          gap: "6px",
        }}
      >
        <Tooltip title="Email">
          <div
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
            }}
          >
            <EmailIcon
              style={{
                color: theme.palette.success.main,
              }}
            />
            <ProtectedElement source="email">
              <TextField
                style={{ color: theme.palette.text.secondary }}
                source="email"
                label="resources.common.labels.email"
              />
            </ProtectedElement>
          </div>
        </Tooltip>
        <Tooltip title="Phone number">
          <div
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
            }}
          >
            <PhoneIcon
              style={{
                color: theme.palette.success.main,
              }}
            />
            <ProtectedElement source="phoneNumber">
              <TextField
                source="phoneNumber"
                label="resources.common.labels.phone"
              />
            </ProtectedElement>
          </div>
        </Tooltip>
        <Tooltip title="Groups">
          <div
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
              color: theme.palette.text.secondary,
            }}
          >
            <GroupIcon
              style={{
                color: theme.palette.success.main,
              }}
            />
            <ProtectedElement sortable={false} source="groups">
              <ArrayField
                record={record}
                source="groups"
                label="resources.common.labels.groups"
                displayedElementsCount={1}
                emptyArrayDisplay="No groups"
              />
            </ProtectedElement>
          </div>
        </Tooltip>
        <FunctionField<DriverListDto & RaRecord>
          source="mobileAppType"
          label="resources.admin-drivers.labels.mobAppType"
          render={(record) => (
            <Box className="mobile-app-container">
              {record?.mobileAppType === "Android" ? (
                <>
                  <Tooltip title="Android">
                    <div
                      style={{
                        display: "flex",
                        gap: "6px",
                        alignItems: "center",
                      }}
                    >
                      <AndroidOutlinedIcon
                        style={{
                          color: theme.palette.success.main,
                        }}
                      />
                      <Typography variant="body2">
                        {` ${record.mobileAppVersion}`}
                      </Typography>
                    </div>
                  </Tooltip>
                </>
              ) : record?.mobileAppType === "AppleIos" ? (
                <>
                  <Tooltip title="Apple">
                    <div
                      style={{
                        display: "flex",
                        gap: "6px",
                        alignItems: "center",
                      }}
                    >
                      <AppleIcon
                        style={{
                          color: theme.palette.success.main,
                        }}
                      />
                      <Typography variant="body2">
                        {` ${record.mobileAppVersion}`}
                      </Typography>
                    </div>
                  </Tooltip>
                </>
              ) : (
                <Tooltip title="The driver hasn't logged in yet">
                  <HelpOutlineOutlinedIcon
                    style={{
                      color: theme.palette.success.main,
                    }}
                  />
                </Tooltip>
              )}
            </Box>
          )}
        />
        <div
          style={{
            marginTop: "8px",
            display: "flex",
            flexDirection: "row",
            gap: "4px",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <Typography
            variant="body2"
            style={{ fontWeight: "600", color: theme.palette.text.secondary }}
          >
            Personal allowed:
          </Typography>
          <ProtectedElement source="personalAllowed">
            <FunctionField<DriverListDto & RaRecord>
              source="personalAllowed"
              headerClassName="centered-content"
              label="resources.admin-drivers.labels.personal"
              render={(record) => (
                <Box className="centered-content">
                  {record?.personalAllowed ? (
                    <CheckBoxIcon
                      className={record.status}
                      style={{
                        color:
                          themeName === "dark"
                            ? theme.palette.success.dark
                            : theme.palette.success.light,
                      }}
                    />
                  ) : (
                    <IndeterminateCheckBoxIcon
                      className={record?.status}
                      style={{
                        color:
                          themeName === "dark"
                            ? theme.palette.error.dark
                            : theme.palette.error.light,
                      }}
                    />
                  )}
                </Box>
              )}
            />
          </ProtectedElement>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "4px",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <Typography
            variant="body2"
            style={{ fontWeight: "600", color: theme.palette.text.secondary }}
          >
            Yard move allowed:
          </Typography>
          <ProtectedElement source="yardMoveAllowed">
            <FunctionField<DriverListDto & RaRecord>
              source="yardMoveAllowed"
              headerClassName="centered-content"
              label="resources.admin-drivers.labels.yardMove"
              render={(record) => (
                <Box className="centered-content">
                  {record?.yardMoveAllowed ? (
                    <CheckBoxIcon
                      className={record.status}
                      style={{
                        color:
                          themeName === "dark"
                            ? theme.palette.success.dark
                            : theme.palette.success.light,
                      }}
                    />
                  ) : (
                    <IndeterminateCheckBoxIcon
                      className={record?.status}
                      style={{
                        color:
                          themeName === "dark"
                            ? theme.palette.error.dark
                            : theme.palette.error.light,
                      }}
                    />
                  )}
                </Box>
              )}
            />
          </ProtectedElement>
        </div>
      </div>
    );
  };

  return (
    <div className="content-container">
      <AppbarGroupsMultipleFilter
        selectedGroups={selectedGroups}
        onSelectedGroupsChange={handleSelectedGroupsChange}
      />
      <FieldPermissionsContext.Provider value={fieldPermissions}>
        <DefaultProLogsList
          empty={<ProLogsEmpty iconType={AssignmentIndOutlinedIcon} />}
          className="content-list"
          title="resources.admin-drivers.name"
          bulkActionButtons={false}
          actions={<ListActions />}
          filters={<ListFilter />}
          filter={
            selectedGroups.length ? { groupIds: selectedGroups.join(",") } : {}
          }
          sort={{ field: "driverName", order: "ASC" }}
          {...props}
        >
          <Responsive
            small={
              <SimpleList
                className="simple-list-wrapper"
                linkType="edit"
                primaryText={(record) => (
                  <RenderPrimaryTextForSimpleList record={record} />
                )}
                secondaryText={(record) => (
                  <RenderSecondaryTextForSimpleList record={record} />
                )}
                rightIcon={(record) => {
                  return (
                    <ActionsField
                      hasShow={props.hasShow}
                      {...props}
                      record={record}
                      confirmDeactivate
                      confirmTitle="resources.admin-drivers.labels.confirmTitle"
                      confirmContent="resources.admin-drivers.labels.confirmContent"
                      editable={() =>
                        permissions &&
                        permissions[permissionNames.admin.drivers.main] &&
                        permissions[permissionNames.admin.drivers.update]
                      }
                      canActivateOrDeactivate={() =>
                        permissions &&
                        permissions[permissionNames.admin.drivers.main] &&
                        permissions[permissionNames.admin.drivers.update]
                      }
                    >
                      <ViewDriverPasswordAction>
                        <MenuItem className="actions-menu-item">
                          <ListItemIcon>
                            <VisibilityOutlinedIcon />
                          </ListItemIcon>
                          <ListItemText>
                            {translate(
                              "resources.admin-drivers.labels.viewPassword"
                            )}
                          </ListItemText>
                        </MenuItem>
                      </ViewDriverPasswordAction>
                      <ChangeDriverPasswordAction />
                    </ActionsField>
                  );
                }}
              />
            }
            medium={
              <Datagrid>
                <TextField
                  source="driverId"
                  label="resources.drivers.labels.driverId"
                />
                <ProtectedElement source="driverName">
                  <TextField
                    source="driverName"
                    label="resources.admin-drivers.labels.displayName"
                  />
                </ProtectedElement>
                <ProtectedElement source="email">
                  <TextField
                    source="email"
                    label="resources.common.labels.email"
                  />
                </ProtectedElement>
                <ProtectedElement source="phoneNumber">
                  <TextField
                    source="phoneNumber"
                    label="resources.common.labels.phone"
                  />
                </ProtectedElement>
                <ProtectedElement sortable={false} source="groups">
                  <ArrayField
                    source="groups"
                    label="resources.common.labels.groups"
                    displayedElementsCount={1}
                    emptyArrayDisplay="No groups"
                  />
                </ProtectedElement>
                <ProtectedElement source="personalAllowed">
                  <FunctionField<DriverListDto & RaRecord>
                    source="personalAllowed"
                    headerClassName="centered-content"
                    label="resources.admin-drivers.labels.personal"
                    render={(record) => (
                      <Box className="centered-content">
                        {record?.personalAllowed ? (
                          <CheckBoxIcon
                            className={record.status}
                            style={{
                              color:
                                themeName === "dark"
                                  ? theme.palette.success.dark
                                  : theme.palette.success.light,
                            }}
                          />
                        ) : (
                          <IndeterminateCheckBoxIcon
                            className={record?.status}
                            style={{
                              color:
                                themeName === "dark"
                                  ? theme.palette.error.dark
                                  : theme.palette.error.light,
                            }}
                          />
                        )}
                      </Box>
                    )}
                  />
                </ProtectedElement>
                <ProtectedElement source="yardMoveAllowed">
                  <FunctionField<DriverListDto & RaRecord>
                    source="yardMoveAllowed"
                    headerClassName="centered-content"
                    label="resources.admin-drivers.labels.yardMove"
                    render={(record) => (
                      <Box className="centered-content">
                        {record?.yardMoveAllowed ? (
                          <CheckBoxIcon
                            className={record.status}
                            style={{
                              color:
                                themeName === "dark"
                                  ? theme.palette.success.dark
                                  : theme.palette.success.light,
                            }}
                          />
                        ) : (
                          <IndeterminateCheckBoxIcon
                            className={record?.status}
                            style={{
                              color:
                                themeName === "dark"
                                  ? theme.palette.error.dark
                                  : theme.palette.error.light,
                            }}
                          />
                        )}
                      </Box>
                    )}
                  />
                </ProtectedElement>
                <ProtectedElement source="drivingCycles">
                  <ArrayField
                    sortable={false}
                    label="resources.admin-drivers.labels.cycleRules"
                    source="drivingCycles"
                    displayedElementsCount={1}
                    emptyArrayDisplay="No cycle rules set"
                  />
                </ProtectedElement>
                <FunctionField<DriverListDto & RaRecord>
                  source="status"
                  headerClassName="centered-content"
                  label="resources.common.labels.active"
                  render={(record) => (
                    <Box className="centered-content">
                      {record?.status === "Active" ? (
                        <CheckBoxIcon
                          className={record.status}
                          style={{
                            color:
                              themeName === "dark"
                                ? theme.palette.success.dark
                                : theme.palette.success.light,
                          }}
                        />
                      ) : (
                        <IndeterminateCheckBoxIcon
                          className={record?.status}
                          style={{
                            color:
                              themeName === "dark"
                                ? theme.palette.error.dark
                                : theme.palette.error.light,
                          }}
                        />
                      )}
                    </Box>
                  )}
                />
                <FunctionField<DriverListDto & RaRecord>
                  source="mobileAppType"
                  label="resources.admin-drivers.labels.mobAppType"
                  render={(record) => (
                    <Box className="mobile-app-container">
                      {record?.mobileAppType === "Android" ? (
                        <>
                          <AndroidOutlinedIcon />
                          <Typography variant="body2">
                            {` ${record.mobileAppVersion}`}
                          </Typography>
                        </>
                      ) : record?.mobileAppType === "AppleIos" ? (
                        <>
                          <AppleIcon />
                          <Typography variant="body2">
                            {` ${record.mobileAppVersion}`}
                          </Typography>
                        </>
                      ) : (
                        <Tooltip title="The driver hasn't logged in yet">
                          <HelpOutlineOutlinedIcon />
                        </Tooltip>
                      )}
                    </Box>
                  )}
                />
                <ActionsField
                  hasShow={props.hasShow}
                  confirmDeactivate
                  confirmTitle="resources.admin-drivers.labels.confirmTitle"
                  confirmContent="resources.admin-drivers.labels.confirmContent"
                  editable={() =>
                    permissions &&
                    permissions[permissionNames.admin.drivers.main] &&
                    permissions[permissionNames.admin.drivers.update]
                  }
                  canActivateOrDeactivate={() =>
                    permissions &&
                    permissions[permissionNames.admin.drivers.main] &&
                    permissions[permissionNames.admin.drivers.update]
                  }
                >
                  <ViewDriverPasswordAction>
                    <MenuItem className="actions-menu-item">
                      <ListItemIcon>
                        <VisibilityOutlinedIcon />
                      </ListItemIcon>
                      <ListItemText>
                        {translate(
                          "resources.admin-drivers.labels.viewPassword"
                        )}
                      </ListItemText>
                    </MenuItem>
                  </ViewDriverPasswordAction>
                  <ChangeDriverPasswordAction />
                </ActionsField>
              </Datagrid>
            }
          />
        </DefaultProLogsList>
      </FieldPermissionsContext.Provider>
    </div>
  );
};
